import {
	AfterViewInit,
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import {
	CodeGenReportFilterOptions,
	CodeGenReportFilters,
} from 'src/app/models/data/reports';
import { CodeGenReport } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import { CodeGenReportDialogComponent } from '../../code-gen-report-dialog/code-gen-report-dialog.component';
import { MatPaginator } from '@angular/material/paginator';
import { UserProfileService } from '../../../../services/user-profile.service';
import { AppNavigationHeaderComponent } from '../../../../components/navigation-header/app-navigation-header.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { BreadcrumbLabelDirective } from '../../../../directives/breadcrumb-label.directive';
import { DataSetTableComponent } from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '@angular/common';
import {
	AccessDatePipe,
	ShortTimezonePipe,
} from '../../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-code-gen-report-table',
	templateUrl: './code-gen-report-table.component.html',
	styleUrls: ['./code-gen-report-table.component.scss'],
	imports: [
		AppNavigationHeaderComponent,
		DataSetComponent_1,
		BreadcrumbLabelDirective,
		DataSetToolbarComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatIcon,
		MatTooltip,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DatePipe,
		AccessDatePipe,
		ShortTimezonePipe,
		DataSetLoadingRowComponent,
	],
})
export class CodeGenReportTableComponent
	implements AfterViewInit, OnInit, OnDestroy
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	displayedColumns = [
		'serialNumber',
		'deviceOwner',
		'generatedBy',
		'assignedTo',
		'propertyName',
		'deviceLocation',
		'dateGenerated',
		'dateValidUtcStart',
		'dateValidUtcEnd',
		'reason',
	];
	dataSource = new MatTableDataSource<CodeGenReport>();
	private filterSubscription?: Subscription;

	reportFilters: CodeGenReportFilters;
	compiledFilters: QueryParam[] = [];
	hasFilters = false;
	filterOptions?: CodeGenReportFilterOptions;
	isHaspCodeGenFeatureEnabled: boolean = false;
	isCareCompany = false;

	breadcrumbLabel = $localize`:@@codeGenReportTable-breadcrumbLabel:Code Gen Report`;

	generateCodeGenReportLoadingKey = ApiLoadingKeys.generateCodeGenReport;
	generateCodeGenReportCsvLoadingKey = ApiLoadingKeys.generateCodeGenReportCsv;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly apiService: ApiService,
		private readonly userProfileService: UserProfileService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService
	) {
		this.reportFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.reportFilters;
		this.compiledFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.compiledFilters;
		this.hasFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.hasFilters;
		this.filterOptions =
			this.router.lastSuccessfulNavigation?.extras?.state?.filterOptions;
	}

	async ngOnInit() {
		await this.featureFlagCheck();
	}

	ngAfterViewInit(): void {
		this.dataSet.applyFilters();
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.generateCodeGenReport([
						...filter,
						...(this.compiledFilters ?? []),
					]);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});
		this.checkCareCompany()
			.then(() => {
				if (this.isCareCompany) {
					this.displayedColumns.splice(3, 0, 'clientName', 'clientIdn');
				}
			})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		if (activeCompanyId) {
			const companyData = await this.apiService.getCompanyById(activeCompanyId);
			this.isCareCompany = companyData?.company?.companyType === 'care';
		}
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	async featureFlagCheck(): Promise<void> {
		this.isHaspCodeGenFeatureEnabled = (
			await this.apiService.getFeatureFlagByName(FeatureFlagName.webHaspCodes)
		)?.value;
		if (this.isHaspCodeGenFeatureEnabled) {
			this.displayedColumns = [...['codeTypeId'], ...this.displayedColumns];
		}
	}

	getShortenedText(text: string, length: number): string {
		if (text.length > length) {
			return text.substring(0, 50) + '...';
		}
		return text;
	}

	onRefineFiltersClicked(): void {
		this.dialog
			.open(CodeGenReportDialogComponent, {
				panelClass: 'drawer-dialog',
				data: {
					reportFilters: this.reportFilters,
					filterOptions: this.filterOptions,
				},
			})
			.afterClosed()
			.subscribe((_: any) => {
				this.dataSet.applyFilters();
			});
	}

	async onDownloadClicked(): Promise<void> {
		const fileName = this.hasFilters
			? 'filteredCodeGenReport'
			: 'codeGenReport';
		await this.apiService.generateCodeGenReportCsv(
			fileName,
			this.compiledFilters
		);
	}

	rowId(row: CodeGenReport): string {
		return row?.odcId?.toString() || 'unknown-id';
	}
}
